import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Loader, Crown } from 'lucide-react';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('success');
      setMessage('Your subscription has been activated!');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    try {
      const response = await apiClient.get(
        `/subscription/checkout/status/${sessionId}`
      );

      if (response.data.payment_status === 'paid' || response.data.already_processed) {
        setStatus('success');
        setMessage('Your premium subscription is now active!');
      } else if (attempts < maxAttempts) {
        // Poll again after 2 seconds
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setStatus('error');
        setMessage('Payment is taking longer than expected. Please check your email.');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
      setMessage('Error verifying payment. Please contact support if charged.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-red-50 px-4 py-12">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            {status === 'checking' && (
              <Loader className="w-16 h-16 text-green-600 animate-spin" />
            )}
            {status === 'success' && (
              <div className="relative">
                <Crown className="w-16 h-16 text-yellow-500" />
                <CheckCircle className="w-8 h-8 text-green-600 absolute -bottom-1 -right-1 bg-white rounded-full" />
              </div>
            )}
            {status === 'error' && (
              <Crown className="w-16 h-16 text-gray-400" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold font-heading">
            {status === 'checking' && 'Processing Payment...'}
            {status === 'success' && 'Welcome to Speak Algerian!'}
            {status === 'error' && 'Payment Issue'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-600">{message}</p>

          {status === 'success' && (
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ✨ Your subscription is active! You now have access to:
                </p>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• All 250+ vocabulary words</li>
                  <li>• All 5 interactive games</li>
                  <li>• Progress tracking</li>
                  <li>• Future features & updates</li>
                </ul>
              </div>

              <Button
                onClick={() => navigate('/vocabulary')}
                className="w-full bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700"
              >
                Start Learning Now
              </Button>
            </div>
          )}

          {status === 'error' && (
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="w-full"
            >
              Back to Pricing
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
